import {
  DocumentTextIcon,
  FolderIcon,
  EllipsisVerticalIcon,
  ChatBubbleBottomCenterIcon,
} from "@heroicons/react/24/outline";
import { CalendarCheck2Icon } from "lucide-react";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { DashboardDriveTableUpload } from "@/components/feature/tables/dashboard_cards/DashboardDriveTableUpload";
import { fakeUsers } from "@/views/home/calendars/mocks/calendarMocks";
import { Link } from "react-router";
import React from "react";

// TypeScript interfaces for User and FileItem
interface User {
  id: string;
  name: string;
  avatar: string;
}

interface FileItem {
  id: string;
  name: string;
  type: "file" | "folder";
  dateCreated: string;
  owner: User;
  location: string;
}

const fakeFiles: FileItem[] = [
  {
    id: "1",
    name: "Abacus Delta LTD vs Clover Papers LTD",
    type: "folder",
    dateCreated: "2025-06-14",
    owner: fakeUsers[0],
    location: "Shared",
  },
  {
    id: "2",
    name: "Research on AI in Legal Industry",
    type: "folder",
    dateCreated: "2025-06-15",
    owner: fakeUsers[1],
    location: "My Drive",
  },
  {
    id: "3",
    name: "Abacus Delta LTD vs Clover Offices LTD - Hearings",
    type: "folder",
    dateCreated: "2025-06-16",
    owner: fakeUsers[0],
    location: "Shared",
  },
  {
    id: "4",
    name: "Abacus - Financial Statements 2022-2023",
    type: "folder",
    dateCreated: "2025-06-17",
    owner: fakeUsers[1],
    location: "My Drive",
  },
  {
    id: "5",
    name: "Contracts 2019-2023 Comparison",
    type: "folder",
    dateCreated: "2025-06-23",
    owner: fakeUsers[0],
    location: "Shared",
  },
  {
    id: "10",
    name: "Project - Financial Report Q1 2023",
    type: "folder",
    dateCreated: "2025-06-23",
    owner: fakeUsers[1],
    location: "My Drive",
  },
  {
    id: "13",
    name: "Legal Document - Contract Agreement.pdf",
    type: "file",
    dateCreated: "2025-06-23",
    owner: fakeUsers[2],
    location: "Shared",
  },
  {
    id: "14",
    name: "Meeting Notes - Project Kickoff.docx",
    type: "file",
    dateCreated: "2024-06-23",
    owner: fakeUsers[3],
    location: "My Drive",
  },
  {
    id: "15",
    name: "Legal Document - NDA.pdf",
    type: "file",
    dateCreated: "2024-06-23",
    owner: fakeUsers[2],
    location: "Shared",
  },
  {
    id: "16",
    name: "Meeting Notes - Client Meeting.docx",
    type: "file",
    dateCreated: "2024-06-23",
    owner: fakeUsers[3],
    location: "My Drive",
  },
  {
    id: "17",
    name: "Legal Document - Settlement Agreement.pdf",
    type: "file",
    dateCreated: "2024-06-23",
    owner: fakeUsers[2],
    location: "Shared",
  },
  {
    id: "18",
    name: "Meeting Notes - Internal Team Update.docx",
    type: "file",
    dateCreated: "2024-06-23",
    owner: fakeUsers[3],
    location: "My Drive",
  },
  {
    id: "19",
    name: "Legal Document - Court Filing.pdf",
    type: "file",
    dateCreated: "2024-06-23",
    owner: fakeUsers[2],
    location: "Shared",
  },
  {
    id: "20",
    name: "Meeting Notes - Strategy Session.docx",
    type: "file",
    dateCreated: "2024-06-23",
    owner: fakeUsers[3],
    location: "My Drive",
  },
  // more file_upload can be added
];

// Single FileItem component
const FileItem: React.FC<{ item: FileItem }> = ({ item }) => (
  <div className="grid grid-cols-12 items-center p-4 text-sm hover:bg-gray-100">
    <div className="col-span-6 flex flex-col items-start text-ui-heading-text">
      <div className="flex items-center">
        {item.type === "folder" ? (
          <FolderIcon className="mr-2 h-5 w-5" />
        ) : (
          <DocumentTextIcon className="mr-2 h-5 w-5" />
        )}
        {item.name}
      </div>
      <div className="py-2 text-xs text-desert-sand-800">{item.dateCreated}</div>
    </div>

    <div className="col-span-6 flex justify-around">
      <Link to="../deadline_guardian/1">
        <button
          type="button"
          className="inline-flex items-center gap-x-1.5 rounded-md bg-desert-sand-100 px-3 py-2 text-sm font-semibold shadow-sm hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >
          <CalendarCheck2Icon className="h-5 w-5" />
          Deadline Guardian
        </button>
      </Link>
      <button
        type="button"
        className="inline-flex items-center gap-x-1.5 rounded-md bg-desert-sand-100 px-3 py-2 text-sm font-semibold text-midnight-blue-700 shadow-sm hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
      >
        <ChatBubbleBottomCenterIcon className="h-5 w-5" />
        Q&A
      </button>
      <button className="p-1">
        <EllipsisVerticalIcon className="h-5 w-5" />
      </button>
    </div>
  </div>
);

// FileList component that renders a list of FileItems with headers
const FileList: React.FC<{ items: FileItem[] }> = ({ items }) => {
  const sortedItems = [...items].sort((a, b) => (a.type === "folder" && b.type !== "folder" ? -1 : 1));
  return (
    <div className="divide-y divide-gray-100 divide-opacity-60 rounded-lg bg-white py-2">
      <div className="grid grid-cols-12 p-2 text-sm font-medium text-gray-500">
        <div className="col-span-6">Name</div>
        <div className="col-span-6">Actions</div>
      </div>
      {sortedItems.map(item => (
        <FileItem key={item.id} item={item} />
      ))}
    </div>
  );
};

// Main Page Component
export const DashboardDriveTable: React.FC = () => {
  // mocking the file_upload for now, so no  need to set up a set function for useState
  const [files] = React.useState<FileItem[]>(fakeFiles);
  const [searchQuery, setSearchQuery] = React.useState<string>("");
  // mocking the limit for now
  const [limit] = React.useState<number>(5);

  const filteredFiles = React.useMemo(
    () =>
      files
        .filter(
          file =>
            file.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            file.location.toLowerCase().includes(searchQuery.toLowerCase()),
        )
        .slice(0, limit),
    [files, searchQuery, limit],
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  return (
    <div className="mx-auto p-8">
      <div className="flex items-center justify-between p-2">
        <div className="flex items-center">
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearch}
              placeholder="Search for existing folders and files"
              className="min-w-96 max-w-full rounded-md border border-gray-300 bg-white px-4 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 transform">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
          </div>
        </div>
        <div className="mb-0 flex items-center">
          <div className="flex space-x-4">
            <DashboardDriveTableUpload />
          </div>
        </div>
      </div>
      <div className="mt-4">
        <FileList items={filteredFiles} />
      </div>
    </div>
  );
};
